import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  completeOnboardingStep,
  ensureOnboarding,
  finalizeOnboarding,
  ONBOARDING_MODULES,
  ONBOARDING_STEPS,
  onboardingStepMeta,
  ACTIVITY_TYPES,
  activityTypeMeta,
  recommendedModulesForActivity,
  type CoreOnboarding,
} from '@/lib/coreOnboarding';
import { getBusinessProfile } from '@/lib/coreBusinessProfile';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function OnboardingTab({ commerceId, org }: Props) {
  const [onboarding, setOnboarding] = useState<CoreOnboarding | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    activity: '', businessName: org.commercial_name || '', city: '', phone: org.phone || '',
    email: org.email || '', description: '', logo: '', profession: '',
  });
  const [modules, setModules] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const ob = await ensureOnboarding(org.id, org.tenant_id);
      setOnboarding(ob);
      if (ob?.data && typeof ob.data === 'object') {
        setForm((f) => ({ ...f, ...(ob.data as Record<string, string>) }));
      }
      setModules(ob?.activated_modules || []);
      const profile = await getBusinessProfile(org.id);
      if (profile) setForm((f) => ({ ...f, businessName: f.businessName || profile.name || '', city: f.city || profile.city || '', description: f.description || profile.description || '' }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id, org.tenant_id]);

  useEffect(() => { load(); }, [load]);

  const completed = new Set(onboarding?.completed_steps || []);
  const progress = Math.round((completed.size / ONBOARDING_STEPS.length) * 100);

  const markStep = async (stepKey: string, data?: Record<string, unknown>) => {
    setSaving(true);
    try {
      const ob = await completeOnboardingStep({ organizationId: org.id, tenantId: org.tenant_id, stepKey, data });
      setOnboarding(ob);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const toggleModule = (key: string) => {
    setModules((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const handleActivityChange = (key: string) => {
    setForm((f) => ({ ...f, activity: key }));
    if (key) {
      // Auto-sélection des modules recommandés pour ce type d'activité.
      setModules(recommendedModulesForActivity(key));
      markStep('activity', { activity: key });
    }
  };

  const finalize = async () => {
    setFinalizing(true);
    setError('');
    try {
      await finalizeOnboarding({
        commerceId,
        organizationId: org.id,
        commercialName: form.businessName || org.commercial_name || `Commerce ${commerceId}`,
        activityCategory: form.activity ? activityTypeMeta(form.activity).label : null,
        businessType: form.activity ? activityTypeMeta(form.activity).label : null,
        city: form.city,
        country: org.country,
        phone: form.phone,
        email: form.email,
        description: form.description,
        logo: form.logo,
        displayName: form.businessName,
        profession: form.profession || activityTypeMeta(form.activity).label,
        modules,
      });
      setDone(true);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Finalisation impossible.');
    } finally {
      setFinalizing(false);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Onboarding</span> : créez votre activité pas à pas. À la fin, Zifek crée automatiquement
        votre <span className="font-semibold text-foreground-700">Business</span>, votre premier <span className="font-semibold text-foreground-700">établissement</span>,
        votre <span className="font-semibold text-foreground-700">profil professionnel</span> et active les modules choisis. Rien n’est obligatoire : vous pouvez revenir plus tard.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-foreground-900">Progression</span>
          <span className="text-sm font-bold text-primary-600">{progress}%</span>
        </div>
        <div className="h-2 rounded-full bg-background-100 overflow-hidden">
          <div className="h-full bg-primary-500 transition-all" style={{ width: `${progress}%` }}></div>
        </div>
        {onboarding?.status === 'completed' && (
          <p className="mt-2 text-xs text-primary-700 flex items-center gap-1.5"><i className="ri-checkbox-circle-line"></i> Activité créée et configurée.</p>
        )}
      </div>

      {/* Étapes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {ONBOARDING_STEPS.map((s) => {
          const isDone = completed.has(s.key);
          const isCurrent = onboarding?.current_step === s.key;
          return (
            <div key={s.key} className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${isCurrent ? 'border-primary-300 bg-primary-50/50' : 'border-background-200/70 bg-background-50'}`}>
              <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500'}`}>
                <i className={isDone ? 'ri-check-line' : s.icon}></i>
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-800 truncate">{s.label}{s.optional && <span className="text-[10px] text-foreground-400 ml-1">optionnel</span>}</p>
                <p className="text-[11px] text-foreground-400 truncate">{s.desc}</p>
              </div>
              {!isDone && (
                <button onClick={() => markStep(s.key)} disabled={saving} className="text-[11px] px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
                  Valider
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Formulaire */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-building-2-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Votre activité</h4>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Type d’activité</span>
              <select className={INPUT} value={form.activity} onChange={(e) => handleActivityChange(e.target.value)}>
                <option value="">Choisir…</option>
                {ACTIVITY_TYPES.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
              {form.activity && (
                <span className="mt-1 block text-[11px] text-primary-700">
                  <i className="ri-magic-line mr-1"></i>{recommendedModulesForActivity(form.activity).length} module(s) recommandé(s) présélectionné(s) ci-dessous.
                </span>
              )}
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Nom de l’entreprise</span>
              <input className={INPUT} value={form.businessName} onChange={(e) => setForm({ ...form, businessName: e.target.value })} placeholder="Ex : Boulangerie Al Amal" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Ville</span>
              <input className={INPUT} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Ex : Casablanca" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Téléphone</span>
              <input className={INPUT} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+212 …" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Email</span>
              <input className={INPUT} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="contact@…" />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-foreground-600 mb-1">Logo (URL)</span>
              <input className={INPUT} value={form.logo} onChange={(e) => setForm({ ...form, logo: e.target.value })} placeholder="https://…" />
            </label>
          </div>
          <label className="block">
            <span className="block text-xs font-medium text-foreground-600 mb-1">Description</span>
            <textarea className={`${INPUT} min-h-[80px]`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Décrivez votre activité en quelques mots…" maxLength={500}></textarea>
          </label>
          <button onClick={() => markStep('business_name', form)} disabled={saving} className="text-xs px-4 py-1.5 rounded-full bg-background-100 text-foreground-700 font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
            Enregistrer ces informations
          </button>
        </div>
      </div>

      {/* Modules */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-apps-2-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Activer les modules nécessaires</h4>
        </div>
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {ONBOARDING_MODULES.map((m) => {
            const active = modules.includes(m.key);
            return (
              <button key={m.key} onClick={() => toggleModule(m.key)} className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-colors cursor-pointer ${active ? 'border-primary-300 bg-primary-50/50' : 'border-background-200/70 bg-background-50 hover:bg-background-100/60'}`}>
                <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500'}`}><i className={m.icon}></i></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-foreground-800 truncate">{m.label}</p>
                  <p className="text-[11px] text-foreground-400 truncate">{m.desc}</p>
                </div>
                <i className={active ? 'ri-checkbox-circle-fill text-primary-600' : 'ri-checkbox-blank-circle-line text-foreground-300'}></i>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-center gap-3">
        <button onClick={finalize} disabled={finalizing || !form.businessName.trim()} className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
          {finalizing ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-rocket-2-line"></i>} Créer mon activité
        </button>
        {done && <span className="text-sm text-primary-700 flex items-center gap-1.5"><i className="ri-checkbox-circle-line"></i> Business, établissement et profil créés.</span>}
      </div>
    </div>
  );
}